import React from 'react';
import { Platform } from 'react-native';
import { registerRootComponent } from 'expo';
import { enableLegacyWebImplementation } from 'react-native-gesture-handler';
import * as WebBrowser from "expo-web-browser";
import { configureProductionLogging } from './Config';
if (Platform.OS !== 'web') {
  require('@tamagui/native/setup-teleport');
}

console.log('index.js loaded. Platform.OS:', Platform.OS);
// Configure production logging FIRST - before any other code runs
// This ensures console logging is disabled in production web builds
configureProductionLogging();

// Import React 19 polyfills FIRST on web
if (Platform.OS === 'web') {
  require('./src/polyfills/react-dom-findDOMNode.web');
  // Polyfill for gesture handler withTestId on web
  require('./src/polyfills/gesture-handler-withTestId.web');
}

// Service worker: registered once from App.tsx (/sw.js). Legacy registerServiceWorker.js removed.

// Robust auth session completion with QuotaExceededError recovery
try {
  WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true });
} catch (e) {
  if (e?.name === 'QuotaExceededError' || e?.message?.includes('Quota') || e?.message?.includes('exceeded the quota')) {
    console.warn('QuotaExceededError caught during auth session completion, clearing persist:root and retrying...');
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('persist:root');
        WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true });
        console.log('Successfully recovered from QuotaExceededError');
      } catch (e2) {
        console.warn('Retry failed after clearing persist:root:', e2);
      }
    }
  } else {
    console.warn('maybeCompleteAuthSession threw an error:', e);
  }
}

// Popup tabs that cannot close (embedded browsers, COOP) must not mount a logged-out app:
// it would overwrite the shared persist:root that the signed-in tab rehydrates from.
let oauthCallbackHandoff = null;

// Robust fallback to ensure popup window closes on web and notifies the opener
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  try {
    const {
      completeOAuthPopupSessionIfNeeded,
      stashMainTabOAuthCallbackIfNeeded,
      isOAuthCallbackUrl,
      isOAuthPopupWindow,
      isMobileWebBrowser,
      waitForOAuthCallbackHandoff,
    } = require('./src/utils/oauthWebPopupCompletion');

    const isCallbackLoad = isOAuthCallbackUrl();
    const handledPopup = completeOAuthPopupSessionIfNeeded();
    if (isCallbackLoad) {
      oauthCallbackHandoff = waitForOAuthCallbackHandoff();
    }

    if (!handledPopup) {
      const search = window.location.search || '';
      const hash = window.location.hash || '';
      if (stashMainTabOAuthCallbackIfNeeded()) {
        // Main tab received OAuth redirect — login UI recovers from storage.
      } else if (isOAuthCallbackUrl() && !isOAuthPopupWindow()) {
        // Legacy stale callback without stashed URL — strip hash so login page renders.
        try {
          window.history.replaceState(null, '', window.location.pathname + search);
        } catch (_) {}
      } else if (isOAuthCallbackUrl() && !window.opener && isMobileWebBrowser()) {
        // Mobile in-app browser only: Universal Links may fail — fall back to custom scheme.
        const nativeUrl = `com.anonymous.smartsaas://${window.location.pathname.replace(/^\/+/, '')}${search}${hash}`;
        window.location.href = nativeUrl;
      }
    }
  } catch (e) {
    console.warn('Custom auth session completion failed:', e);
  }
}

// Global/login CSS is linked in web/index.html; other sheets load via src/utils/loadWebStyles.ts.

// if (__DEV__ && Platform.OS === 'web') {
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   whyDidYouRender(React, {
//     trackAllPureComponents: true,
//   });
// }

if (Platform.OS === 'web') { 
  enableLegacyWebImplementation(true) 
};

if (Platform.OS !== 'web') {
  try {
    const { initFirebaseAnalytics } = require('./src/utils/firebaseAnalytics');
    initFirebaseAnalytics();
  } catch (e) {
    console.warn('[FirebaseAnalytics] bootstrap failed:', e);
  }
}

// App (and everything that captures HOST from @api/constant) must load only
// after the native API origin is resolved, so it is required lazily.
const loadApp = () => require('./App').default;

function NativeApiOriginGate(props) {
  const [AppComponent, setAppComponent] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const { resolveApiOrigin } = require('./src/utils/apiRegion');
    resolveApiOrigin()
      .catch((e) => console.warn('[apiRegion] origin resolution failed:', e))
      .finally(() => {
        if (!cancelled) setAppComponent(() => loadApp());
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return AppComponent ? React.createElement(AppComponent, props) : null;
}

if (Platform.OS !== 'web') {
  registerRootComponent(NativeApiOriginGate);
} else if (oauthCallbackHandoff) {
  oauthCallbackHandoff
    .then((handedOff) => {
      if (handedOff) {
        const { closeOAuthPopupWindow } = require('./src/utils/oauthWebPopupCompletion');
        closeOAuthPopupWindow();
        return;
      }
      document.documentElement.style.visibility = '';
      registerRootComponent(loadApp());
    })
    .catch(() => registerRootComponent(loadApp()));
} else {
  registerRootComponent(loadApp());
}